import useUser from '@teetsh/app/src/queries/user/useUser';
import React, {
  useState,
  useLayoutEffect,
  createContext,
  useContext,
  useEffect,
} from 'react';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { UserProfile } from '@teetsh/app/src/types/user';
import { useLogin } from '@teetsh/app/src/queries/user/useLogin';
import useLogout from '@teetsh/app/src/queries/user/useLogout';
import { ErrorPage } from '@teetsh/app/src/components/ErrorPage';
import {
  RegisterGoogleRequestPayload,
  useRegisterGoogle,
} from '@teetsh/app/src/queries/user/useRegisterGoogle';
import { useLoginGoogle } from '@teetsh/app/src/queries/user/useLoginGoogle';
import identifyUserToExternalServices from '@teetsh/app/src/queries/user/identifyUserToExternalServices';
import { usePostHog } from 'posthog-js/react';
import useSignup2023, {
  Signup2023RequestPayload,
} from '@teetsh/app/src/queries/user/useSignup2023';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface Auth {
  login: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<any>;
  loginGoogle: ({ tokenId }: { tokenId: string }) => Promise<any>;
  signup: (p: Signup2023RequestPayload) => Promise<any>;
  registerGoogle: (p: RegisterGoogleRequestPayload) => Promise<any>;
  logout: () => Promise<any>;
  user: UserProfile;
}

export const AuthContext = createContext<Auth | undefined>(undefined);

function AuthProvider(props) {
  const [firstAttemptFinished, setFirstAttemptFinished] = useState(false);
  const { i18n } = useTranslation();
  const posthog = usePostHog();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const {
    data: user,
    isFetched,
    isSuccess,
    isInitialLoading,
    isError,
    error,
    refetch,
  } = useUser();

  useLayoutEffect(() => {
    if (isFetched) {
      setFirstAttemptFinished(true);
    }
    if (isSuccess) {
      const ignoreUserLocale = urlSearchParams.has('lng');
      if (i18n.language !== user.locale && !ignoreUserLocale) {
        i18n.changeLanguage(user.locale);
      }
      // It allows to keep the language when the user refreshes the page instead of using the browser language
      if (localStorage.getItem('i18nextLng') !== user.locale) {
        localStorage.setItem('i18nextLng', user.locale);
      }
      identifyUserToExternalServices(user, posthog);
    }
  }, [isFetched, isError, isSuccess]);
  const triggerLogin = useLogin();
  const triggerLoginGoogle = useLoginGoogle();
  const triggerSignup = useSignup2023();
  const triggerRegisterGoogle = useRegisterGoogle();
  const triggerLogout = useLogout();

  useEffect(() => {
    const handleStorageChange = event => {
      if (event.key === 'logout-event') {
        triggerLogout({ disableLogoutOtherTabs: true });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [triggerLogout]);
  if (!firstAttemptFinished) {
    if (isInitialLoading) {
      return <SpinnerComponent />;
    }
    const statusCode =
      error && (error as any).response ? (error as any).response.status : '';
    if (isError && statusCode !== 401) {
      return <ErrorPage />;
    }
  }

  const login = (p: { email: string; password: string }) =>
    triggerLogin(p).then(() => refetch());
  const loginGoogle = (p: { tokenId: string }) =>
    triggerLoginGoogle(p).then(refetch);
  const signup = (p: Signup2023RequestPayload) =>
    triggerSignup(p).then(() => refetch());
  const registerGoogle = (p: RegisterGoogleRequestPayload) =>
    triggerRegisterGoogle(p).then(() => refetch());
  const logout = () => triggerLogout().then(() => refetch());

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        signup,
        loginGoogle,
        registerGoogle,
      }}
      {...props}
    />
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
